import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ChevronRight, Wallet } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { UnitProvider, useUnitContext } from '@/contexts/UnitContext';
import { setupTreasurerModule } from '@/lib/treasurerApi';
import { showError, showSuccess } from '@/lib/swal';

interface FormState {
  treasurerName: string;
  treasurerEmail: string;
  worshipfulMasterName: string;
  worshipfulMasterEmail: string;
  financialYearStartMonth: string;
  financialYearStartDay: string;
  subscriptionDueDateMonth: string;
  subscriptionDueDateDay: string;
  capitationRatePounds: string;
  largeTransactionThresholdPounds: string;
  meetingDates: string;
  prorateSubscriptionsForMidYearLeavers: boolean;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function poundsToPence(pounds: string): number | null {
  const cleaned = pounds.trim().replace(/[£,\s]/g, '');
  if (cleaned === '' || !/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole = '0', frac = ''] = cleaned.split('.');
  const padded = (frac + '00').slice(0, 2);
  return Number.parseInt(whole, 10) * 100 + Number.parseInt(padded, 10);
}

function parseMeetingDates(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
}

function Inner() {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const {
    activeUnit,
    availableUnitsLoading,
    treasurerSettings,
    treasurerSettingsLoading,
  } = useUnitContext();

  useEffect(() => {
    if (!unitId) return;
    if (availableUnitsLoading) return;
    if (!activeUnit) navigate('/units', { replace: true });
  }, [unitId, activeUnit, availableUnitsLoading, navigate]);

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>({
    treasurerName: '',
    treasurerEmail: '',
    worshipfulMasterName: '',
    worshipfulMasterEmail: '',
    financialYearStartMonth: '1',
    financialYearStartDay: '1',
    subscriptionDueDateMonth: '1',
    subscriptionDueDateDay: '1',
    capitationRatePounds: '',
    largeTransactionThresholdPounds: '500.00',
    meetingDates: '',
    prorateSubscriptionsForMidYearLeavers: false,
  });

  // Pre-fill from existing settings (when treasurer is updating, not initialising).
  useEffect(() => {
    if (!treasurerSettings) return;
    setForm((prev) => ({
      ...prev,
      treasurerName: treasurerSettings.treasurerName || prev.treasurerName,
      treasurerEmail: treasurerSettings.treasurerEmail || prev.treasurerEmail,
      worshipfulMasterName: treasurerSettings.worshipfulMasterName || prev.worshipfulMasterName,
      worshipfulMasterEmail: treasurerSettings.worshipfulMasterEmail || prev.worshipfulMasterEmail,
      financialYearStartMonth: String(treasurerSettings.financialYearStart?.month ?? prev.financialYearStartMonth),
      financialYearStartDay: String(treasurerSettings.financialYearStart?.day ?? prev.financialYearStartDay),
      subscriptionDueDateMonth: String(treasurerSettings.subscriptionDueDate?.month ?? prev.subscriptionDueDateMonth),
      subscriptionDueDateDay: String(treasurerSettings.subscriptionDueDate?.day ?? prev.subscriptionDueDateDay),
      capitationRatePounds: typeof treasurerSettings.grandLodgeCapitationRatePence === 'number'
        ? (treasurerSettings.grandLodgeCapitationRatePence / 100).toFixed(2)
        : prev.capitationRatePounds,
      largeTransactionThresholdPounds: typeof treasurerSettings.largeTransactionThresholdPence === 'number'
        ? (treasurerSettings.largeTransactionThresholdPence / 100).toFixed(2)
        : prev.largeTransactionThresholdPounds,
      meetingDates: Array.isArray(treasurerSettings.meetingDates)
        ? treasurerSettings.meetingDates.join('\n')
        : prev.meetingDates,
      prorateSubscriptionsForMidYearLeavers:
        treasurerSettings.prorateSubscriptionsForMidYearLeavers ?? prev.prorateSubscriptionsForMidYearLeavers,
    }));
  }, [treasurerSettings]);

  // Pre-fill name/email from logged-in user.
  useEffect(() => {
    if (userProfile && !form.treasurerName) {
      setForm((prev) => ({
        ...prev,
        treasurerName:
          [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ').trim() ||
          userProfile.name ||
          '',
        treasurerEmail: userProfile.email || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  const isUpdate = treasurerSettings?.isInitialised === true;

  const validation = useMemo(() => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.treasurerName.trim()) errors.treasurerName = 'Required';
    if (!form.treasurerEmail.trim()) errors.treasurerEmail = 'Required';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.treasurerEmail)) errors.treasurerEmail = 'Invalid email';

    if (form.worshipfulMasterEmail.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.worshipfulMasterEmail)) {
      errors.worshipfulMasterEmail = 'Invalid email';
    }

    const fyMonth = parseInt(form.financialYearStartMonth, 10);
    const fyDay = parseInt(form.financialYearStartDay, 10);
    if (!Number.isFinite(fyMonth) || fyMonth < 1 || fyMonth > 12) errors.financialYearStartMonth = '1-12';
    if (!Number.isFinite(fyDay) || fyDay < 1 || fyDay > 31) errors.financialYearStartDay = '1-31';

    const subMonth = parseInt(form.subscriptionDueDateMonth, 10);
    const subDay = parseInt(form.subscriptionDueDateDay, 10);
    if (!Number.isFinite(subMonth) || subMonth < 1 || subMonth > 12) errors.subscriptionDueDateMonth = '1-12';
    if (!Number.isFinite(subDay) || subDay < 1 || subDay > 31) errors.subscriptionDueDateDay = '1-31';

    if (form.capitationRatePounds.trim() && poundsToPence(form.capitationRatePounds) === null) {
      errors.capitationRatePounds = 'Invalid amount';
    }
    if (poundsToPence(form.largeTransactionThresholdPounds) === null) {
      errors.largeTransactionThresholdPounds = 'Invalid amount';
    }

    return errors;
  }, [form]);

  const isValid = Object.keys(validation).length === 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || !unitId) return;
    setSubmitting(true);
    try {
      const capitationPence = form.capitationRatePounds.trim()
        ? poundsToPence(form.capitationRatePounds) ?? 0
        : 0;
      const largePence = poundsToPence(form.largeTransactionThresholdPounds) ?? 50000;

      const result = await setupTreasurerModule({
        unitId,
        treasurerName: form.treasurerName.trim(),
        treasurerEmail: form.treasurerEmail.trim(),
        worshipfulMasterName: form.worshipfulMasterName.trim() || undefined,
        worshipfulMasterEmail: form.worshipfulMasterEmail.trim() || undefined,
        financialYearStart: {
          month: parseInt(form.financialYearStartMonth, 10),
          day: parseInt(form.financialYearStartDay, 10),
        },
        subscriptionDueDate: {
          month: parseInt(form.subscriptionDueDateMonth, 10),
          day: parseInt(form.subscriptionDueDateDay, 10),
        },
        grandLodgeCapitationRatePence: capitationPence,
        largeTransactionThresholdPence: largePence,
        meetingDates: parseMeetingDates(form.meetingDates),
        prorateSubscriptionsForMidYearLeavers: form.prorateSubscriptionsForMidYearLeavers,
      });

      showSuccess(
        result.initialised
          ? 'Treasurer module set up successfully'
          : 'Settings updated',
      );
      navigate(`/u/${unitId}/dashboard`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  }

  if (availableUnitsLoading || treasurerSettingsLoading || !activeUnit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-3">
          <Wallet className="h-6 w-6 text-emerald-600" />
          <div className="text-lg font-semibold">
            {isUpdate ? 'Treasurer settings' : 'Set up Treasurer module'}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-slate-600 mb-6">
          {isUpdate
            ? 'Update settings for this Lodge. All amounts are in pounds and pence.'
            : `These details unlock the Treasurer features for ${activeUnit.name} No. ${activeUnit.number}. You can change every value later from Settings.`}
        </p>

        <form onSubmit={handleSubmit} className="space-y-8 bg-white rounded-xl border border-slate-200 p-6">
          {/* Treasurer */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-3">Treasurer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Name"
                value={form.treasurerName}
                onChange={(v) => setForm((p) => ({ ...p, treasurerName: v }))}
                error={validation.treasurerName}
              />
              <Field
                label="Email"
                type="email"
                value={form.treasurerEmail}
                onChange={(v) => setForm((p) => ({ ...p, treasurerEmail: v }))}
                error={validation.treasurerEmail}
              />
            </div>
          </section>

          {/* Worshipful Master */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-1">
              Worshipful Master <span className="text-slate-400 font-normal text-sm">(optional)</span>
            </h2>
            <p className="text-sm text-slate-500 mb-3">
              Used for large-transaction alert recipients. The WM gets read-only access to reports.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Name"
                value={form.worshipfulMasterName}
                onChange={(v) => setForm((p) => ({ ...p, worshipfulMasterName: v }))}
              />
              <Field
                label="Email"
                type="email"
                value={form.worshipfulMasterEmail}
                onChange={(v) => setForm((p) => ({ ...p, worshipfulMasterEmail: v }))}
                error={validation.worshipfulMasterEmail}
              />
            </div>
          </section>

          {/* Financial year */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-3">Financial year</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label="Start month"
                value={form.financialYearStartMonth}
                onChange={(v) => setForm((p) => ({ ...p, financialYearStartMonth: v }))}
                options={MONTHS.map((name, i) => ({ value: String(i + 1), label: name }))}
                error={validation.financialYearStartMonth}
              />
              <Field
                label="Start day"
                type="number"
                value={form.financialYearStartDay}
                onChange={(v) => setForm((p) => ({ ...p, financialYearStartDay: v }))}
                error={validation.financialYearStartDay}
              />
            </div>
          </section>

          {/* Subscriptions */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-3">Subscriptions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label="Due date — month"
                value={form.subscriptionDueDateMonth}
                onChange={(v) => setForm((p) => ({ ...p, subscriptionDueDateMonth: v }))}
                options={MONTHS.map((name, i) => ({ value: String(i + 1), label: name }))}
                error={validation.subscriptionDueDateMonth}
              />
              <Field
                label="Due date — day"
                type="number"
                value={form.subscriptionDueDateDay}
                onChange={(v) => setForm((p) => ({ ...p, subscriptionDueDateDay: v }))}
                error={validation.subscriptionDueDateDay}
              />
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.prorateSubscriptionsForMidYearLeavers}
                onChange={(e) =>
                  setForm((p) => ({ ...p, prorateSubscriptionsForMidYearLeavers: e.target.checked }))
                }
              />
              Pro-rate subscriptions for members who leave mid-year
            </label>
          </section>

          {/* Money */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-3">Amounts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Grand Lodge capitation rate (£ per member per year)"
                value={form.capitationRatePounds}
                onChange={(v) => setForm((p) => ({ ...p, capitationRatePounds: v }))}
                placeholder="e.g. 28.50"
                error={validation.capitationRatePounds}
              />
              <Field
                label="Large transaction alert threshold (£)"
                value={form.largeTransactionThresholdPounds}
                onChange={(v) => setForm((p) => ({ ...p, largeTransactionThresholdPounds: v }))}
                placeholder="500.00"
                error={validation.largeTransactionThresholdPounds}
              />
            </div>
          </section>

          {/* Meeting dates */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-1">Meeting dates</h2>
            <p className="text-sm text-slate-500 mb-3">
              One date per line in YYYY-MM-DD format. You can change these any time.
            </p>
            <textarea
              rows={5}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
              placeholder="2026-01-15&#10;2026-03-19&#10;2026-05-21"
              value={form.meetingDates}
              onChange={(e) => setForm((p) => ({ ...p, meetingDates: e.target.value }))}
            />
          </section>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={() => navigate(isUpdate ? `/u/${unitId}/settings` : `/u/${unitId}/dashboard`)}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || submitting}
              className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-700"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isUpdate ? 'Save settings' : 'Complete setup'}
              {!submitting && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  error?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${
          error ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-emerald-500'
        }`}
      />
      {error && <span className="text-xs text-red-600 mt-1">{error}</span>}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  error?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none bg-white ${
          error ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-emerald-500'
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <span className="text-xs text-red-600 mt-1">{error}</span>}
    </label>
  );
}

export default function SetupWizardPage() {
  const { unitId } = useParams<{ unitId: string }>();
  return (
    <UnitProvider unitIdFromRoute={unitId ?? null}>
      <Inner />
    </UnitProvider>
  );
}
